import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { paymentService } from '../apis/paymentService';
import { usePricing } from '../context/PricingContext';
import { subscriptionService } from '../apis/subscriptionService';
import { Header } from "../components/header";
import { toast } from 'react-hot-toast';
import { authService } from '../apis/authService';
import {
  CreditCard,
  ShieldCheck,
  Sparkles,
  Mic,
  FileText,
  Lock,
  ArrowRight,
  Loader2,
  Mail,
  BarChart3,
  Stethoscope,
  RefreshCw,
  Check,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { VBotLogo } from '../components/ui/vbot-logo';
import {
  DEFAULT_PLAN_KEY,
  computeAnnualSavingsPercent,
} from '../utils/pricingDisplay';
import '../components/billing/payment-receipt.css';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const slideInRight = {
  hidden: { opacity: 0, x: 28 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { delay: 0.15, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const CHECKOUT_PERKS = [
  { icon: Mic, label: 'Live transcription' },
  { icon: FileText, label: 'SOAP notes' },
  { icon: Sparkles, label: 'AI assistance' },
  { icon: Lock, label: 'HIPAA-ready' },
];

const SUPPORT_EMAIL = 'support@virtualscribe.ai';

/** Paid tiers shown on checkout. Elite stays in plan metadata for later. */
const CHECKOUT_VISIBLE_PLAN_KEYS = ['starter', 'professional'];

const CHECKOUT_PLAN_LABELS = {
  starter: 'Starter',
  professional: 'Professional',
  elite: 'Elite',
};

const PLAN_CARD_HIGHLIGHTS = {
  starter: [
    '50 SOAP notes / month',
    '30-day note history',
    'Live transcription',
  ],
  professional: [
    'Unlimited charts & notes',
    'Magic Editor & HPI modes',
    '7-day free trial',
  ],
  elite: [
    'Everything in Professional',
    'Early access & priority requests',
    'Same-day EMR turnaround',
  ],
};

/** 2×2 info cards under plan picker — keyed by plan */
const PLAN_INFO_BY_KEY = {
  starter: {
    noteLimit: '50 SOAP notes / month',
    emrInput: 'Not included on Starter',
  },
  professional: {
    noteLimit: 'Unlimited charts & notes',
    emrInput: 'Optional EMR input · $1.99 / chart',
  },
  elite: {
    noteLimit: 'Unlimited charts & notes',
    emrInput: 'EMR input · $1.29 / chart · same-day',
  },
};

function isCheckoutVisiblePlan(planKey) {
  return CHECKOUT_VISIBLE_PLAN_KEYS.includes(String(planKey || '').toLowerCase());
}

function getPlanInfoCards(planKey) {
  const info = PLAN_INFO_BY_KEY[planKey] || PLAN_INFO_BY_KEY.professional;
  return [
    {
      icon: BarChart3,
      title: 'Chart limit',
      body: info.noteLimit,
    },
    {
      icon: Stethoscope,
      title: 'EMR input',
      body: info.emrInput,
    },
    {
      icon: Mail,
      title: 'Support',
      body: SUPPORT_EMAIL,
      href: `mailto:${SUPPORT_EMAIL}`,
    },
    {
      icon: RefreshCw,
      title: 'Billing help',
      body: SUPPORT_EMAIL,
      href: `mailto:${SUPPORT_EMAIL}`,
    },
  ];
}

const formatMoney = (value) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
};

// Payment form component
const PaymentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [paymentsMessage, setPaymentsMessage] = useState('');
  const [canConnectQuickBooks, setCanConnectQuickBooks] = useState(false);
  const [providers, setProviders] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('stripe');
  const {
    getCheckoutAmount,
    getAmount,
    getPlanMeta,
    currency,
    plans: pricingPlans,
  } = usePricing();
  const [planKey, setPlanKey] = useState(DEFAULT_PLAN_KEY);
  const [billingPeriod, setBillingPeriod] = useState('annually');
  const [seatCount, setSeatCount] = useState(1);
  const [switchToAnnualIntent, setSwitchToAnnualIntent] = useState(false);

  const planKeyOptions = useMemo(() => {
    const active = (pricingPlans || []).filter(
      (p) => p.is_active !== false && parseFloat(p.amount) > 0
    );
    const available = new Set(
      active.map((p) => String(p.plan_key || '').toLowerCase())
    );
    const keys = CHECKOUT_VISIBLE_PLAN_KEYS.filter(
      (key) => !available.size || available.has(key)
    );
    const visibleKeys = keys.length ? keys : CHECKOUT_VISIBLE_PLAN_KEYS;
    return visibleKeys.map((key) => {
      const row = active.find(
        (p) => String(p.plan_key || '').toLowerCase() === key
      );
      const label =
        row?.display_name?.replace(/\s*\([^)]*\)\s*$/, '').trim() ||
        CHECKOUT_PLAN_LABELS[key] ||
        key;
      return { value: key, label };
    });
  }, [pricingPlans]);

  const checkoutPlanCards = useMemo(
    () =>
      planKeyOptions.map((opt) => {
        const meta = getPlanMeta(opt.value, billingPeriod);
        const seats = meta?.perSeat
          ? Math.max(meta.minSeats || 2, seatCount)
          : 1;
        return {
          value: opt.value,
          label: opt.label,
          unitAmount: getAmount(
            opt.value,
            billingPeriod,
            meta?.perSeat ? 1 : seats
          ),
          checkoutTotal: getCheckoutAmount(opt.value, billingPeriod, seats),
          popular: opt.value === DEFAULT_PLAN_KEY,
          highlights: PLAN_CARD_HIGHLIGHTS[opt.value] || [],
        };
      }),
    [
      planKeyOptions,
      billingPeriod,
      seatCount,
      getAmount,
      getCheckoutAmount,
      getPlanMeta,
    ]
  );

  const annualSavings = useMemo(() => {
    const savingsKey = planKeyOptions.some((o) => o.value === DEFAULT_PLAN_KEY)
      ? DEFAULT_PLAN_KEY
      : planKeyOptions[0]?.value;
    if (!savingsKey) return 0;
    return computeAnnualSavingsPercent(
      getAmount(savingsKey, 'monthly', 1),
      getAmount(savingsKey, 'annually', 1)
    );
  }, [planKeyOptions, getAmount]);

  const handleSelectPlan = (pk) => {
    setPlanKey(pk);
    const meta = getPlanMeta(pk, billingPeriod);
    if (meta?.perSeat && seatCount < (meta.minSeats || 2)) {
      setSeatCount(meta.minSeats || 2);
    }
  };

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    amount: '',
    description: '',
  });
  const [accountEmail, setAccountEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await authService.getMe();
        if (response.success) {
          const email = response.data.email || '';
          setAccountEmail(email);
          setFormData(prev => ({
            ...prev,
            customerName: `${response.data.profile.firstName} ${response.data.profile.lastName}`,
            customerEmail: email,
          }));
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        if (error.response && error.response.status === 401) {
          navigate('/signin');
        }
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const applyConnectionConfig = useCallback((data) => {
    setPaymentsEnabled(Boolean(data?.paymentsEnabled));
    setPaymentsMessage(data?.paymentsMessage || data?.message || '');
    setCanConnectQuickBooks(Boolean(data?.canConnect));
    setProviders(data?.providers || null);
    setSelectedProvider(data?.recommendedProvider || 'stripe');
  }, []);

  useEffect(() => {
    const loadConnection = async () => {
      try {
        const res = await paymentService.getConnectionConfig();
        const data = res.data?.body?.data || res.data?.data || res.data;
        applyConnectionConfig(data);
      } catch (err) {
        console.error('Failed to load payment connection config:', err);
      }
    };
    loadConnection();

    try {
      const saved = sessionStorage.getItem('vbot_payment_plan');
      if (saved) {
        const { planKey: pk, billingPeriod: bp, seatCount: sc } = JSON.parse(saved);
        if (pk) {
          setPlanKey(isCheckoutVisiblePlan(pk) ? pk : DEFAULT_PLAN_KEY);
        }
        if (bp) setBillingPeriod(bp);
        if (sc) setSeatCount(sc);
        sessionStorage.removeItem('vbot_payment_plan');
      }
      const switchIntent = sessionStorage.getItem('vbot_payment_intent');
      if (switchIntent === 'switch_to_annual') {
        setSwitchToAnnualIntent(true);
        setBillingPeriod('annually');
        sessionStorage.removeItem('vbot_payment_intent');
      }
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (switchToAnnualIntent) {
      setBillingPeriod('annually');
    }
  }, [switchToAnnualIntent]);

  useEffect(() => {
    if (!planKeyOptions.length) return;
    if (!planKeyOptions.some((o) => o.value === planKey)) {
      const fallback = planKeyOptions.some((o) => o.value === DEFAULT_PLAN_KEY)
        ? DEFAULT_PLAN_KEY
        : planKeyOptions[0].value;
      setPlanKey(fallback);
    }
  }, [planKeyOptions, planKey]);

  useEffect(() => {
    const amount = getCheckoutAmount(planKey, billingPeriod, seatCount);
    if (amount != null) {
      setFormData((prev) => ({ ...prev, amount: String(amount) }));
    }
  }, [getCheckoutAmount, planKey, billingPeriod, seatCount]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const realm = queryParams.get('realmId');
    const connected = queryParams.get('connected');
    const urlError = queryParams.get('error');

    const paymentId = queryParams.get('paymentId');
    const chargeId = queryParams.get('chargeId');
    if (paymentId && chargeId) {
      check3DSCompletion(paymentId, chargeId);
    }

    if (connected === 'true' && realm) {
      toast.success('QuickBooks connected for admin sync.');
      paymentService.getConnectionConfig().then((res) => {
        const data = res.data?.body?.data || res.data?.data || res.data;
        applyConnectionConfig(data);
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlError) {
      const messages = {
        oauth_failed: 'QuickBooks connection failed. Please try again.',
        oauth_state_invalid: 'QuickBooks connection expired. Please try again.',
        missing_parameters: 'QuickBooks connection was incomplete.',
        token_exchange_failed: 'QuickBooks authorization failed.',
      };
      setError(messages[urlError] || 'QuickBooks connection error.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    localStorage.removeItem('qb_auth_token');
  }, [location, applyConnectionConfig]);

  useEffect(() => {
    if (selectedProvider === 'quickbooks') {
      setSelectedProvider('stripe');
    }
  }, [selectedProvider]);

  const handleCheckout = async () => {
    const email = formData.customerEmail.trim();
    if (!formData.customerName || !email) {
      toast.error('Please complete your profile information');
      return;
    }
    if (!isEmailValid(email)) {
      toast.error('Please enter a valid receipt email address');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const session = await paymentService.createCheckoutSession({
        customerName: formData.customerName,
        customerEmail: email,
        description: formData.description,
        planKey,
        billingPeriod,
        seatCount: getPlanMeta(planKey, billingPeriod)?.perSeat ? seatCount : 1,
        provider: selectedProvider,
      });

      if (session?.redirectUrl) {
        window.location.href = session.redirectUrl;
        return;
      }

      // A checkout started earlier for this same plan was already paid —
      // send the user to the return page instead of erroring out.
      if (session?.alreadyCompleted && session?.returnUrl) {
        window.location.href = session.returnUrl;
        return;
      }

      throw new Error('Checkout session was not returned');
    } catch (err) {
      const msg =
        err.response?.data?.body?.message ||
        err.response?.data?.error ||
        err.message ||
        'Could not start checkout';
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  const check3DSCompletion = async (paymentId, chargeId) => {
    try {
      setLoading(true);
      const response = await paymentService.check3DSStatus(paymentId, chargeId);
      
      const data = response.data || response;
      if (data.success && data.status === 'completed') {
        toast.success('3D Secure authentication completed successfully!');
        localStorage.removeItem('pending_3ds_payment');
        try {
          await subscriptionService.upgradeToPremium(paymentId);
        } catch (upgradeErr) {
          console.warn('Subscription upgrade:', upgradeErr);
        }
        navigate(`/payment-receipt/${paymentId}`);
      } else {
        toast.error('3D Secure authentication failed or is still pending.');
      }
    } catch (error) {
      console.error('3DS completion check error:', error);
      toast.error('Failed to verify 3D Secure authentication status.');
    } finally {
      setLoading(false);
    }
  };

  const displayAmount = getCheckoutAmount(planKey, billingPeriod, seatCount);
  const planMeta = getPlanMeta(planKey, billingPeriod);
  const unitAmount = getAmount(
    planKey,
    billingPeriod,
    planMeta?.perSeat ? 1 : seatCount
  );
  const selectedPlanLabel =
    planKeyOptions.find((o) => o.value === planKey)?.label ||
    CHECKOUT_PLAN_LABELS[planKey] ||
    planKey;
  const effectiveSeats = planMeta?.perSeat ? seatCount : 1;
  const isPerSeat = Boolean(planMeta?.perSeat);

  if (isLoading) {
    return (
      <div className="vps-checkout-page vps-checkout-loading">
        <div className="vps-checkout-loader" aria-label="Loading checkout">
          <span className="vps-checkout-loader-ring" />
          <VBotLogo size="sm" />
        </div>
      </div>
    );
  }

  const checkoutLabel =
    selectedProvider === 'quickbooks'
      ? (loading ? 'Redirecting…' : 'Continue to payment')
      : (loading ? 'Redirecting to Stripe…' : 'Continue to payment');

  return (
    <motion.div
      className="vps-checkout-page vps-checkout-page--animated"
      initial={{ opacity: 0, y: 24, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    >
      <header className="vps-checkout-hero">
        <div className="vps-checkout-hero-orbs" aria-hidden="true">
          <span className="vps-orb vps-orb-1" />
          <span className="vps-orb vps-orb-2" />
          <span className="vps-orb vps-orb-3" />
        </div>
        <motion.div
          className="vps-ticket-header-inner"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={0}
        >
          <motion.div
            initial={{ rotate: -8, scale: 0.85 }}
            animate={{ rotate: 0, scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.1 }}
          >
            <VBotLogo size="md" className="vps-ticket-brand-logo" />
          </motion.div>
          <div className="vps-ticket-brand-text">
            <p className="vps-ticket-brand-eyebrow">VirtualScribe</p>
            <h2 className="vps-ticket-brand-title">Subscribe to VirtualScribe</h2>
          </div>
        </motion.div>
        <motion.p
          className="vps-ticket-brand-sub"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          Select your plan and complete checkout securely. Your subscription renews automatically each billing period. You&apos;ll receive an official receipt on confirmation.
        </motion.p>
        <motion.ul
          className="vps-checkout-perks"
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.06, delayChildren: 0.25 } },
          }}
        >
          {CHECKOUT_PERKS.map(({ icon: Icon, label }) => (
            <motion.li
              key={label}
              className="vps-checkout-perk"
              variants={fadeUp}
              custom={0}
              whileHover={{ scale: 1.04, y: -2 }}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </motion.li>
          ))}
        </motion.ul>
      </header>

      <div className="vps-checkout-body">
        <div className="vps-checkout-card">
          {error && <div className="vps-alert vps-alert-error">{error}</div>}

          {switchToAnnualIntent && (
            <div className="vps-alert vps-alert-info mb-4">
              <strong>Switching to annual billing</strong>
              <p>
                Complete checkout to move your subscription to annual billing.
                Your remaining monthly time will be kept and one year will be added from your current end date.
              </p>
            </div>
          )}

          {!paymentsEnabled && (
            <div className="vps-alert vps-alert-warn">
              <strong>Payments not available</strong>
              <p>
                {paymentsMessage ||
                  'Add Stripe API keys on the server, or ask an admin to connect QuickBooks.'}
              </p>
              {canConnectQuickBooks && (
                <button
                  type="button"
                  onClick={() => paymentService.authorizeQuickBooks()}
                  className="mt-3 text-sm font-semibold px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                >
                  Connect QuickBooks (admin)
                </button>
              )}
            </div>
          )}

          {paymentsEnabled && (
            <div className="vps-checkout-split">
              <motion.section
                className="vps-checkout-product"
                aria-label="Product details"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.2 } },
                }}
              >
                <motion.h3 className="vps-checkout-section-title" variants={fadeUp} custom={0}>
                  Subscription
                </motion.h3>

                <motion.div className="vps-checkout-field" variants={fadeUp} custom={0}>
                  <label htmlFor="checkout-provider">Payment method</label>
                  <select
                    id="checkout-provider"
                    value={selectedProvider === 'quickbooks' ? 'stripe' : selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                  >
                    <option value="stripe" disabled={!providers?.stripe?.enabled}>
                      Stripe
                    </option>
                    <option value="quickbooks" disabled>
                      QuickBooks (unavailable)
                    </option>
                  </select>
                </motion.div>

                <motion.div className="vps-checkout-plan-row" variants={fadeUp} custom={1}>
                  <div className="vps-checkout-plan-picker">
                    <div className="vps-checkout-field">
                      <label id="checkout-billing-label">Billing</label>
                      {switchToAnnualIntent ? (
                        <p className="vps-checkout-field-hint">
                          Annual billing is locked for this switch.
                        </p>
                      ) : (
                        <div
                          className="vps-checkout-billing-toggle"
                          role="group"
                          aria-labelledby="checkout-billing-label"
                        >
                          <button
                            type="button"
                            aria-pressed={billingPeriod === 'monthly'}
                            className={billingPeriod === 'monthly' ? 'is-active' : undefined}
                            onClick={() => setBillingPeriod('monthly')}
                          >
                            Monthly
                          </button>
                          <button
                            type="button"
                            aria-pressed={billingPeriod === 'annually'}
                            className={billingPeriod === 'annually' ? 'is-active' : undefined}
                            onClick={() => setBillingPeriod('annually')}
                          >
                            Annual
                            {annualSavings > 0 && (
                              <span className="vps-checkout-billing-save">
                                Save {annualSavings}%
                              </span>
                            )}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="vps-checkout-field">
                      <label id="checkout-plan-label">Plan</label>
                      <div
                        className="vps-checkout-plan-cards"
                        role="radiogroup"
                        aria-labelledby="checkout-plan-label"
                      >
                        {checkoutPlanCards.map((card) => {
                          const selected = planKey === card.value;
                          return (
                            <motion.button
                              key={card.value}
                              type="button"
                              role="radio"
                              aria-checked={selected}
                              aria-label={`${card.label}, ${formatMoney(card.unitAmount)} per month`}
                              className={`vps-checkout-plan-card${selected ? ' is-selected' : ''}${
                                card.popular ? ' is-popular' : ''
                              }`}
                              onClick={() => handleSelectPlan(card.value)}
                              whileHover={{ y: selected ? 0 : -2 }}
                              whileTap={{ scale: 0.985 }}
                            >
                              {card.popular && (
                                <span className="vps-checkout-plan-card-badge">
                                  Most Popular
                                </span>
                              )}
                              <span className="vps-checkout-plan-card-radio" aria-hidden="true" />
                              <span className="vps-checkout-plan-card-name">{card.label}</span>
                              <span className="vps-checkout-plan-card-price">
                                <span className="vps-checkout-plan-card-amount">
                                  {formatMoney(card.unitAmount)}
                                </span>
                                <span className="vps-checkout-plan-card-period">/mo</span>
                              </span>
                              <span className="vps-checkout-plan-card-billed">
                                {billingPeriod === 'annually'
                                  ? `Billed annually · ${formatMoney(card.checkoutTotal)}/yr`
                                  : 'Billed monthly · cancel anytime'}
                              </span>
                              <div className="vps-checkout-plan-card-features">
                                {card.highlights.map((feat) => (
                                  <span key={feat} className="vps-checkout-plan-card-feature">
                                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                                    <span>{feat}</span>
                                  </span>
                                ))}
                              </div>
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>

                    {isPerSeat && (
                      <div className="vps-checkout-field vps-checkout-field--seats">
                        <label htmlFor="checkout-seats">Seats</label>
                        <input
                          id="checkout-seats"
                          type="number"
                          min={planMeta?.minSeats || 2}
                          max={planMeta?.maxSeats || 100}
                          value={seatCount}
                          onChange={(e) =>
                            setSeatCount(
                              Math.max(
                                planMeta?.minSeats || 2,
                                parseInt(e.target.value, 10) ||
                                  planMeta?.minSeats ||
                                  2
                              )
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                </motion.div>

                <motion.div className="vps-checkout-info-grid" variants={fadeUp} custom={2}>
                  {getPlanInfoCards(planKey).map(({ icon: Icon, title, body, href }) => (
                    <div key={title} className="vps-checkout-info-card">
                      <div className="vps-checkout-info-card-head">
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                        <strong>{title}</strong>
                      </div>
                      {href ? (
                        <a href={href} className="vps-checkout-info-card-body vps-checkout-info-card-link">
                          {body}
                        </a>
                      ) : (
                        <p className="vps-checkout-info-card-body">{body}</p>
                      )}
                    </div>
                  ))}
                </motion.div>
              </motion.section>

              <motion.aside
                className="vps-checkout-summary"
                aria-label="Order summary"
                variants={slideInRight}
                initial="hidden"
                animate="visible"
              >
                <div className="vps-checkout-field vps-checkout-field--email vps-checkout-summary-email">
                  <label htmlFor="checkout-receipt-email">Receipt email</label>
                  <input
                    id="checkout-receipt-email"
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, customerEmail: e.target.value }))
                    }
                    placeholder="you@clinic.com"
                    autoComplete="email"
                    spellCheck={false}
                  />
                  {accountEmail && formData.customerEmail.trim() !== accountEmail && (
                    <button
                      type="button"
                      className="vps-checkout-email-reset"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, customerEmail: accountEmail }))
                      }
                    >
                      Use account email
                    </button>
                  )}
                </div>

                <h3 className="vps-checkout-section-title">Order summary</h3>

                <dl className="vps-checkout-summary-lines">
                  <div className="vps-checkout-summary-line">
                    <dt>Plan</dt>
                    <dd>{selectedPlanLabel}</dd>
                  </div>
                  <div className="vps-checkout-summary-line">
                    <dt>Rate</dt>
                    <dd>
                      {formatMoney(unitAmount)}
                      <span className="vps-checkout-summary-muted"> / month</span>
                    </dd>
                  </div>
                  {isPerSeat && (
                    <div className="vps-checkout-summary-line">
                      <dt>Seats</dt>
                      <dd>{effectiveSeats}</dd>
                    </div>
                  )}
                  {isPerSeat && unitAmount != null && (
                    <div className="vps-checkout-summary-line">
                      <dt>Subtotal</dt>
                      <dd>
                        {formatMoney(unitAmount * effectiveSeats)}
                        <span className="vps-checkout-summary-muted"> / month</span>
                      </dd>
                    </div>
                  )}
                  <div className="vps-checkout-summary-line">
                    <dt>Billing</dt>
                    <dd>{billingPeriod === 'annually' ? 'Annual (12 months)' : 'Monthly'}</dd>
                  </div>
                  <div className="vps-checkout-summary-line vps-checkout-summary-total">
                    <dt>Total due today</dt>
                    <dd className="vps-checkout-total-wrap">
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={String(displayAmount)}
                          className="vps-checkout-total-value"
                          initial={{ opacity: 0, y: 10, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.95 }}
                          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                        >
                          {formatMoney(displayAmount)}
                        </motion.span>
                      </AnimatePresence>
                    </dd>
                  </div>
                  <div className="vps-checkout-summary-line">
                    <dt>Currency</dt>
                    <dd>{currency || 'USD'}</dd>
                  </div>
                </dl>

                <div className="vps-checkout-summary-billed">
                  <span className="vps-quick-amount">
                    {billingPeriod === 'annually'
                      ? 'Renews annually until cancelled'
                      : 'Renews monthly until cancelled'}
                  </span>
                </div>

                <div className="vps-checkout-actions vps-checkout-actions--inline">
                  <motion.button
                    type="button"
                    onClick={handleCheckout}
                    disabled={loading || !isEmailValid(formData.customerEmail)}
                    className="vps-btn-done vps-btn-done--checkout bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white shadow-sm hover:shadow-lg transition-all"
                    whileHover={{ scale: loading ? 1 : 1.03, y: loading ? 0 : -2 }}
                    whileTap={{ scale: loading ? 1 : 0.97 }}
                  >
                    <span className="vps-btn-checkout-icon" aria-hidden="true">
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4" />
                      )}
                    </span>
                    <span className="vps-btn-checkout-label">{checkoutLabel}</span>
                    {!loading && (
                      <ArrowRight className="h-4 w-4 vps-btn-checkout-arrow" aria-hidden="true" />
                    )}
                  </motion.button>
                  <p className="vps-checkout-trust">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Secure checkout · Official invoice emailed after payment
                  </p>
                </div>
              </motion.aside>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const PaymentPage = () => (
  <>
    <Header />
    <div className="pay-page-shell pay-page-shell--checkout">
      <div className="pay-page-inner pay-page-inner--checkout">
        <PaymentForm />
      </div>
    </div>
  </>
);

export default PaymentPage;